import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {
    MarkConversionProvider,
    withMarkConversion,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import type {
    LearnableContent,
    StudentAssignment,
} from "@khan/assignments-types";
import {
    addQueryParamsToRelativeUrl,
    COMPLETION_STATES,
    getContent,
    getScore,
} from "@khan/assignments-utils";
import Table, {Column} from "@khan/component-table";
import {isDeprecatedTutoringActivity} from "@khan/khanmigo-chat-history";
import {mediaQueries} from "@khan/shared-styles";

import type {
    CourseLevelTopicPath,
    LessonLevelTopicPath,
} from "#content-library/types/types.ts";
import {findPathUnderNodes} from "#content-library/ui/utils/assignments-util.ts";
import {
    constructURL,
    toContentKind,
} from "#content-library/ui/utils/content-util.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedSpinner from "#shared-components-package/deprecated-spinner.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedIcon from "#shared-styles-package/deprecated-icon.tsx";

import {DateTimeCell} from "../components/table-cells.tsx";
import {contentKindIconMap} from "../constants.ts";

import infoIcon from "@phosphor-icons/core/bold/info-bold.svg";
import externalLinkIcon from "@phosphor-icons/core/regular/arrow-square-out.svg";

type LinkProps = {
    onClick?: () => unknown;
    href: string;
    target?: "_blank";
};

// sync-start:wc-content-id 1798246868 ./apps/writing-coach/src/constants.ts
const WRITING_COACH_CONTENT_ID = "x1e6d165d54a37115";
// sync-end:wc-content-id

const isWritingCoachAssignment = (assignment: StudentAssignment): boolean => {
    const {id} = getContent(assignment);
    return id === WRITING_COACH_CONTENT_ID;
};
// Constant that sets the threshold for whether we encourage the student to
// attempt the assignment again for a better score (we need a perfect score
// to show the confirmation status)
const RETRY_EXERCISE_THRESHOLD = 100;

const getCompletionStateForCurrentStudent = (assignment: StudentAssignment) => {
    // In student assignment table, the totalCompletionStates should only have
    // the student's completion state.
    const {totalCompletionStates} = assignment;

    // totalCompletionStates for student assignment has to exist but in case
    // for weirdness (and also for flow) we check these and return unstarted state.
    if (!totalCompletionStates || !totalCompletionStates[0]) {
        return {
            state: COMPLETION_STATES.UNSTARTED,
        };
    }

    const completionState = totalCompletionStates[0];

    return {
        ...completionState,
        state:
            completionState.state === COMPLETION_STATES.UNSTARTED &&
            completionState.essaySession != null
                ? COMPLETION_STATES.IN_PROGRESS
                : completionState.state,
    };
};

const getStudentScore = compose(getScore, getCompletionStateForCurrentStudent);

const isSameQuestion = (assignment: StudentAssignment) => {
    if (assignment.exerciseConfig) {
        return assignment.exerciseConfig.itemPickerStrategy === "SAME";
    }
    return false;
};

const getReportUrlBase = (assignment: StudentAssignment) => {
    const content = getContent(assignment);

    if (!content || !content.defaultUrlPath || !assignment.id) {
        return "";
    }

    let urlBase = `${content.defaultUrlPath}/report`;
    if (content.kind === "TopicQuiz") {
        urlBase = urlBase.replace("#", "/q/");
    } else if (content.kind === "TopicUnitTest") {
        urlBase = urlBase.replace("#", "/u/");
    }

    return urlBase;
};

const buildContentURLForAssignment = (
    content: LearnableContent,
    assignment: StudentAssignment,
) => {
    const {domainId, courseId, unitId, lessonId} = assignment;
    const topicPaths = (content.topicPaths ?? [])
        .map((topicPath) => {
            if (!topicPath?.path) {
                return null;
            }

            const learnableContent = topicPath.path[topicPath.path.length - 1];

            if (learnableContent.kind === "CourseChallenge") {
                const [domain, course, _] = topicPath.path;
                const result: CourseLevelTopicPath = {
                    pathKind: "course-level-learnable-content",
                    domainId: domain.id,
                    domainSlug: domain.slug,
                    courseId: course.id,
                    courseSlug: course.slug,
                    learnableContentId: learnableContent.id,
                    learnableContentKind: "CourseChallenge",
                    learnableContentSlug: learnableContent.slug,
                };
                return result;
            }

            const [domain, course, unit, lesson, _] = topicPath.path;
            const result: LessonLevelTopicPath = {
                pathKind: "lesson-level-learnable-content",
                domainId: domain.id,
                domainSlug: domain.slug,
                courseId: course.id,
                courseSlug: course.slug,
                unitId: unit.id,
                unitSlug: unit.slug,
                lessonId: lesson.id,
                lessonSlug: lesson.slug,
                learnableContentId: learnableContent.id,
                learnableContentKind: toContentKind(content.kind),
                learnableContentSlug: learnableContent.slug,
            };
            return result;
        })
        .filter(isTruthy);
    const path = findPathUnderNodes(topicPaths, {
        domainId,
        courseId,
        unitId,
        lessonId,
    });
    return path
        ? (constructURL(path) ?? content.defaultUrlPath)
        : content.defaultUrlPath;
};

// Exported only for testing
export const getAssignmentHref = (assignment: StudentAssignment): string => {
    const content = getContent(assignment);
    let href = "";
    if (content.id === WRITING_COACH_CONTENT_ID) {
        return `/writing-coach/go/${assignment.id}`;
    }
    if (
        content.kind === "CourseChallenge" ||
        content.kind === "AIGuideActivity"
    ) {
        href = content.defaultUrlPath;
    } else {
        href = buildContentURLForAssignment(content, assignment);
    }
    if (assignment.studentList?.signupCode) {
        // We always attach the class code when routing to 321.
        // TODO (LP-11877) Replace this with classroom UUID when available
        const queryParams = [
            {
                key: "classCode",
                value: assignment.studentList.signupCode,
            },
        ];

        // With 321, we no longer open different-question Exercises in a modal,
        // so we have to make sure we only add the URL param for same-question
        // Exercises.
        const assignmentKey = assignment.key;
        if (
            assignmentKey &&
            content.kind === "Exercise" &&
            isSameQuestion(assignment)
        ) {
            queryParams.push({key: "assignment", value: assignmentKey});
        }

        if (content.kind === "AIGuideActivity") {
            queryParams.push({key: "assignment", value: assignment.id});
        }

        return addQueryParamsToRelativeUrl(href, queryParams);
    }

    if (
        content.kind === "TopicQuiz" ||
        content.kind === "TopicUnitTest" ||
        content.kind === "CourseChallenge"
    ) {
        href = addQueryParamsToRelativeUrl(href, [
            {
                key: "open",
                value: "1",
            },
        ]);
    } else if (content.kind === "Exercise" && assignment.key) {
        // For exercise URLs, we add an `?assignment=key` parameter. This is
        // used specifically by same-questions assignments, allowing them to
        // identify the shared question-order that is specified by the
        // assignment.
        href = addQueryParamsToRelativeUrl(href, [
            {
                key: "assignment",
                value: assignment.key,
            },
        ]);
    }
    return href;
};

type DefaultProps = {
    filterId: string;
};

type Props = DefaultProps & {
    isLoading: boolean;
    assignments: Array<StudentAssignment>;
    studentKaid: string;
    canUseLearnerActivities: boolean;
    handleRefetchCurrentPage: () => void;
};

const noOpFunctionToTriggerRowHighlighting = () => {};

type State = {
    unsubscribeFromStore: () => void | null | undefined;
};

class StudentAssignmentsTable extends React.Component<Props, State> {
    static defaultProps: DefaultProps = {
        filterId: "active",
    };

    state: State = {
        // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type '() => void | null | undefined'.
        unsubscribeFromStore: undefined,
    };

    componentWillUnmount() {
        const {unsubscribeFromStore} = this.state;

        if (unsubscribeFromStore) {
            unsubscribeFromStore();
            // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type '() => void | null | undefined'.
            this.setState({unsubscribeFromStore: undefined});
        }
    }

    getAssignmentNameEmptyLabel(): string {
        const {filterId} = this.props;

        if (filterId === "active") {
            return t`You don't have any assignments.`;
        }

        // Past assignments
        return t`You don't have any past assignments.`;
    }

    renderAssignmentNameColumn(): React.ReactElement<
        React.ComponentProps<typeof Column>
    > {
        const columnLabel = t`Assignment`;
        const emptyText = this.getAssignmentNameEmptyLabel();

        const emptyPlaceHolder = (
            <LabelMedium style={styles.placeholder}>{emptyText}</LabelMedium>
        );

        return (
            <Column
                label={columnLabel}
                emptyPlaceHolder={emptyPlaceHolder}
                maxWidth={300}
            >
                {this.renderContentCellWithIcon}
            </Column>
        );
    }

    renderContentCell: (assignment: StudentAssignment) => React.ReactNode = (
        assignment: StudentAssignment,
    ) => {
        const enableCourseChallengeAssignmentsV2 = true;
        const {defaultUrlPath, title, kind} = getContent(assignment);
        // Teacher may have specified the assignment title, such as in the case
        // of Khanmigo activity assignments.
        const customTitle = assignment.title;
        const {state} = getCompletionStateForCurrentStudent(assignment);
        const isCourseChallenge = kind === "CourseChallenge";
        const isActivity = kind === "AIGuideActivity";
        const isCompleted =
            state === COMPLETION_STATES.COMPLETED ||
            state === COMPLETION_STATES.LATE;
        const isRetiredActivity =
            isActivity && isDeprecatedTutoringActivity(defaultUrlPath);

        const assignmentTitle = (
            <LabelMedium style={styles.contentTitle}>
                {customTitle && customTitle !== "" ? customTitle : title}
            </LabelMedium>
        );

        // If the assignment is a course challenge and the student has completed it,
        // do not link to the assignment to discourage retrying the assignment.
        // (This case departs from the typical convention because course challenges
        // are often used as a way to assess students so multiple attempts are not encouraged.)
        if (
            enableCourseChallengeAssignmentsV2 &&
            isCourseChallenge &&
            isCompleted
        ) {
            return assignmentTitle;
        }

        // If the assignment is an activity that is retired, do not link to the assignment.
        if (isRetiredActivity) {
            const tooltipText = t`This assignment is not available because the activity is no longer available to students.`;
            return (
                <Tooltip content={tooltipText}>
                    <View style={styles.titleWithIcon}>
                        {assignmentTitle}
                        <Strut size={spacing.xxxSmall_4} />
                        <PhosphorIcon
                            aria-label={tooltipText}
                            icon={infoIcon}
                            color={color.red}
                        />
                    </View>
                </Tooltip>
            );
        }

        // If the assignment is an Activity Assignment but the student does not
        // have access to the Khanmigo, do not link to the assignment.
        if (
            isActivity &&
            !this.props.canUseLearnerActivities &&
            !isWritingCoachAssignment(assignment)
        ) {
            const tooltipText = t`This assignment is not available because you do not have access to Khanmigo.`;
            return (
                <Tooltip content={tooltipText}>
                    <View style={styles.titleWithIcon}>
                        {assignmentTitle}
                        <Strut size={spacing.xxxSmall_4} />
                        <PhosphorIcon
                            aria-label={tooltipText}
                            icon={infoIcon}
                            color={color.red}
                        />
                    </View>
                </Tooltip>
            );
        }

        return (
            <MarkConversionProvider>
                {({publishAnalyticsEvent}) => (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error
                    <Link
                        testId="student-assignment-name-cell"
                        {...this.getLinkProps(assignment)}
                        title={customTitle ?? title}
                        onClick={() => {
                            publishAnalyticsEvent({
                                eventType:
                                    "LoggedInHomepageClassDetailsAssignmentOpened",
                                eventSchemaVersion: 1,
                                redirectToAssignment: "ASSIGNMENT_LINK",
                            });
                        }}
                        style={styles.link}
                        endIcon={
                            isWritingCoachAssignment(assignment) ? (
                                <PhosphorIcon
                                    icon={externalLinkIcon}
                                    size="small"
                                    style={styles.externalLinkIcon}
                                />
                            ) : undefined
                        }
                    >
                        {assignmentTitle}
                    </Link>
                )}
            </MarkConversionProvider>
        );
    };

    renderContentCellWithIcon: (
        assignment: StudentAssignment,
    ) => React.ReactNode = (assignment: StudentAssignment) => {
        const {kind} = getContent(assignment);

        if (!kind) {
            return (
                <LabelMedium style={[styles.contentCell, styles.errorTitle]}>
                    {t`This assignment is not available in your language.`}
                </LabelMedium>
            );
        }

        return (
            <div className={css(styles.contentCell)}>
                <DeprecatedIcon
                    className={css(styles.contentIcon)}
                    icon={contentKindIconMap[kind]}
                    size={12}
                />
                {this.renderContentCell(assignment)}
            </div>
        );
    };

    renderDueDateColumn: () => React.ReactElement<
        React.ComponentProps<typeof Column>
    > = () => (
        <Column label={t`Due date & time`} align="left" id="dueDate">
            {(assignment: StudentAssignment) => {
                const {state} = getCompletionStateForCurrentStudent(assignment);

                return (
                    <LabelMedium>
                        <DateTimeCell
                            isLate={state === COMPLETION_STATES.MISSED}
                            dateTime={assignment.dueDate}
                        />
                    </LabelMedium>
                );
            }}
        </Column>
    );

    renderStatusColumn: () => React.ReactElement<
        React.ComponentProps<typeof Column>
    > = () => (
        <Column label={t`Status`} align="right">
            {(assignment: StudentAssignment) => (
                <div className={css(styles.statusColumn)}>
                    {this.renderStatusForStudentCompletion(assignment)}
                </div>
            )}
        </Column>
    );

    renderStatusForStudentCompletion: (
        assignment: StudentAssignment,
    ) => React.ReactNode = (assignment: StudentAssignment) => {
        const {kind, defaultUrlPath} = getContent(assignment);
        if (!kind) {
            return "";
        }

        const isRetiredActivity =
            kind === "AIGuideActivity" &&
            isDeprecatedTutoringActivity(defaultUrlPath);

        const linkProps = this.getLinkProps(assignment);

        const {state} = getCompletionStateForCurrentStudent(assignment);

        const enableCourseChallengeAssignmentsV2 = true;
        const isCourseChallenge = kind === "CourseChallenge";

        if (isRetiredActivity) {
            switch (state) {
                case COMPLETION_STATES.COMPLETED:
                case COMPLETION_STATES.LATE:
                    return t`Completed`;
                default:
                    return t`Not Completed`;
            }
        }

        switch (state) {
            case COMPLETION_STATES.UNSTARTED:
            case COMPLETION_STATES.IN_PROGRESS:
            case COMPLETION_STATES.MISSED:
                return (
                    <MarkConversionProvider>
                        {({publishAnalyticsEvent}) => (
                            <Button
                                {...linkProps}
                                disabled={
                                    kind === "AIGuideActivity" &&
                                    !this.props.canUseLearnerActivities &&
                                    !isWritingCoachAssignment(assignment)
                                }
                                testId="student-assignment-table-start-button"
                                size="small"
                                onClick={() => {
                                    publishAnalyticsEvent({
                                        eventType:
                                            "LoggedInHomepageClassDetailsAssignmentOpened",
                                        eventSchemaVersion: 1,
                                        redirectToAssignment: "START_BUTTON",
                                    });
                                }}
                            >
                                {state === COMPLETION_STATES.IN_PROGRESS
                                    ? t`Resume`
                                    : t`Start`}
                            </Button>
                        )}
                    </MarkConversionProvider>
                );
            case COMPLETION_STATES.LATE:
            case COMPLETION_STATES.COMPLETED:
                const score = getStudentScore(assignment);

                // Show "Completed" text for any non-scored
                // completed assignment.
                // Non-exercise and legacy exercise submissions won't have a
                // best score.
                if (typeof score !== "number") {
                    return t`Completed`;
                }
                const tryAgainLink = <Link {...linkProps}>{t`Try again`}</Link>;

                const reportUrl = this.buildReportUrl(assignment);
                const scoreText = t`${score}%`;

                let scoreReportLink;

                if (enableCourseChallengeAssignmentsV2) {
                    scoreReportLink = reportUrl ? (
                        <Link href={reportUrl}>{scoreText}</Link>
                    ) : (
                        scoreText
                    );
                } else {
                    scoreReportLink =
                        reportUrl && !isCourseChallenge ? (
                            <Link href={reportUrl}>{scoreText}</Link>
                        ) : (
                            scoreText
                        );
                }

                // If the score is below "pass" threshold,
                // we want to encourage the user to try again.
                // The only exception is course challenges for which
                // we want to discourage retrying since they are
                // often used by teachers as a way to assess students.
                // TODO(CLASS-9259): Remove the feature flag check once it is on by default.
                // Please note that this change is gated behind the feature flag so that
                // we can release the project fast-follows together.
                const showTryAgainLink =
                    score < RETRY_EXERCISE_THRESHOLD &&
                    (!isCourseChallenge ||
                        (isCourseChallenge &&
                            !enableCourseChallengeAssignmentsV2));

                if (showTryAgainLink) {
                    return (
                        <div>
                            {tryAgainLink} | {scoreReportLink}
                        </div>
                    );
                }
                return scoreReportLink;

            default:
                return null;
        }
    };

    getLinkProps: (assignment: StudentAssignment) => LinkProps = (
        assignment: StudentAssignment,
    ) => {
        const href = getAssignmentHref(assignment);
        if (isWritingCoachAssignment(assignment)) {
            return {
                href,
                target: "_blank",
            };
        }
        return {
            href: getAssignmentHref(assignment),
        };
    };

    buildReportUrl: (
        assignment: StudentAssignment,
    ) => string | null | undefined = (assignment: StudentAssignment) => {
        const reportUrlBase = getReportUrlBase(assignment);
        const score = getStudentScore(assignment);

        // if the report url base is empty string (when the
        // content is deleted), we don't want to render broken link
        if (typeof score !== "number" || !reportUrlBase) {
            return;
        }

        const queryParams = [
            {key: "studentScore", value: score.toString()},
            {key: "assignment", value: assignment.id || ""},
        ];
        return addQueryParamsToRelativeUrl(reportUrlBase, queryParams);
    };

    render(): React.ReactNode {
        const {isLoading, assignments} = this.props;

        if (isLoading) {
            return <DeprecatedSpinner />;
        }

        return (
            <Table
                data={assignments}
                rowHeight={48}
                onRowClick={noOpFunctionToTriggerRowHighlighting}
                useCursorPointerOnHover={false}
            >
                {this.renderAssignmentNameColumn()}
                {this.renderDueDateColumn()}
                {this.renderStatusColumn()}
            </Table>
        );
    }
}

const styles = StyleSheet.create({
    placeholder: {
        whiteSpace: "nowrap",
    },
    titleWithIcon: {
        flexDirection: "row",
        alignItems: "center",
    },
    // eslint-disable-next-line react-native/no-unused-styles
    moduleTitlePadding: {
        paddingBottom: 4,
    },
    statusColumn: {
        width: 122,
        whiteSpace: "nowrap",
        color: color.offBlack,
    },
    contentTitle: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        flex: 1,
        [mediaQueries.xsOrSmaller]: {
            width: 120,
            marginLeft: 0,
        },
    },
    errorTitle: {
        maxWidth: 512,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        [mediaQueries.xsOrSmaller]: {
            width: 120,
            marginLeft: 0,
        },
    },
    contentCell: {
        display: "flex",
        alignItems: "center",
    },
    contentIcon: {
        paddingRight: 16,
        opacity: 0.5,
        boxSizing: "content-box",
    },
    externalLinkIcon: {
        marginLeft: spacing.xxxSmall_4,
        verticalAlign: "-10%",
        flex: "0 0 auto",
    },
    link: {
        display: "flex",
        flexDirection: "row",
        whiteSpace: "nowrap",
        overflow: "hidden",
        maxWidth: 256,
    },
});

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof StudentAssignmentsTable,
        React.ComponentProps<typeof StudentAssignmentsTable>
    >
>;

export default compose(withMarkConversion)(
    StudentAssignmentsTable,
) as React.ComponentType<ExportProps>;
